const LOGGED_OUT_QUERY = 'logged_out=1';

export const getLoggedOutReturnTo = (): string => {
  if (typeof window === 'undefined') {
    return '/';
  }

  return `${window.location.origin}/?${LOGGED_OUT_QUERY}`;
};

export const shouldUseFederatedLogout = (): boolean =>
  (process.env.REACT_APP_AUTH0_FEDERATED_LOGOUT || '').toLowerCase() === 'true';

export const getAuth0LogoutParams = (): { returnTo: string; federated?: boolean } => {
  const params: { returnTo: string; federated?: boolean } = {
    returnTo: getLoggedOutReturnTo(),
  };

  if (shouldUseFederatedLogout()) {
    params.federated = true;
  }

  return params;
};

const AUTH0_STATE_KEY_PREFIXES = [
  '@@auth0spajs@@',
  'auth0.',
  '_legacy_auth0.',
  'a0.spajs.txs.',
];

const AUTH0_COOKIE_NAMES = [
  'auth0.is.authenticated',
  '_legacy_auth0.is.authenticated',
  'auth0.organization_hint',
  '_legacy_auth0.organization_hint',
];

const isAuth0StorageKey = (key: string | null): boolean => {
  if (!key) return false;
  return AUTH0_STATE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));
};

const clearStorageByPrefix = (storage: Storage): void => {
  const keysToRemove: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const k = storage.key(i);
    if (isAuth0StorageKey(k)) {
      keysToRemove.push(k as string);
    }
  }
  keysToRemove.forEach((k) => storage.removeItem(k));
};

/**
 * Wipe every Auth0 SDK artifact we can reach from the app origin.
 * Defense-in-depth fallback for cases where the SDK's logout() either
 * never executes its cache-clear step or where a stale localStorage
 * entry survived a previous direct /v2/logout hit.
 */
export const clearLocalAuth0State = (): void => {
  if (typeof window === 'undefined') return;

  try {
    clearStorageByPrefix(window.localStorage);
  } catch (err) {
    console.warn('[authLogout] localStorage clear failed:', err);
  }

  try {
    clearStorageByPrefix(window.sessionStorage);
  } catch (err) {
    console.warn('[authLogout] sessionStorage clear failed:', err);
  }

  try {
    const host = window.location.hostname;
    AUTH0_COOKIE_NAMES.forEach((name) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host};`;
      const apex = host.split('.').slice(-2).join('.');
      if (apex && apex !== host) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${apex};`;
      }
    });
  } catch (err) {
    console.warn('[authLogout] cookie clear failed:', err);
  }
};

/**
 * Build the Auth0 /v2/logout URL directly from env so we can hard-redirect
 * even if the SDK never reaches its window.location.assign().
 */
export const buildAuth0LogoutUrl = (): string => {
  const domain = (
    process.env.REACT_APP_AUTH0_DOMAIN ||
    process.env.REACT_APP_AUTH0_ISSUER_BASE_URL ||
    ''
  )
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '');
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID || '';
  const returnTo = encodeURIComponent(getLoggedOutReturnTo());

  let url = `https://${domain}/v2/logout?client_id=${clientId}&returnTo=${returnTo}`;
  if (shouldUseFederatedLogout()) {
    url += '&federated=true';
  }
  return url;
};

type SdkLogout = (opts?: { logoutParams?: { returnTo: string; federated?: boolean } }) => unknown;

/**
 * Bulletproof logout used by every UI entry point that signs the user out.
 *
 * Sequence:
 *  1. Wipe local Auth0 state (storage + cookies) so a refresh cannot
 *     rehydrate `useAuth0().user` from cache.
 *  2. Schedule a fallback: if the SDK does not navigate within 1500ms,
 *     force `window.location.replace(/v2/logout)` ourselves.
 *  3. Call the SDK's logout() so the happy path still benefits from its
 *     own cookie/dpop cleanup and logout URL building.
 *  4. On any thrown error from the SDK, fall back to the direct redirect
 *     immediately so the user is never left stranded on the dashboard.
 *
 * The fallback is intentionally aggressive: a missed redirect is exactly
 * the symptom that produced the original logout-loop bug.
 */
export const performHardLogout = async (
  sdkLogout: SdkLogout | null | undefined
): Promise<void> => {
  console.log('[authLogout] performHardLogout: start');
  clearLocalAuth0State();

  const directUrl = buildAuth0LogoutUrl();
  let redirected = false;

  const fallbackTimer = window.setTimeout(() => {
    if (redirected) return;
    console.warn('[authLogout] SDK did not redirect in time; forcing /v2/logout');
    redirected = true;
    window.location.replace(directUrl);
  }, 1500);

  try {
    if (typeof sdkLogout === 'function') {
      console.log('[authLogout] calling SDK logout()');
      const maybe = sdkLogout({ logoutParams: getAuth0LogoutParams() });
      if (maybe && typeof (maybe as Promise<unknown>).then === 'function') {
        await maybe;
      }
      console.log('[authLogout] SDK logout() returned (browser should be navigating)');
    } else {
      console.warn('[authLogout] SDK logout unavailable; using direct redirect');
      window.clearTimeout(fallbackTimer);
      redirected = true;
      window.location.replace(directUrl);
    }
  } catch (err) {
    console.error('[authLogout] SDK logout threw, forcing direct redirect:', err);
    window.clearTimeout(fallbackTimer);
    if (!redirected) {
      redirected = true;
      window.location.replace(directUrl);
    }
  }
};
